/**
 * Słownik panelu operatora. Polski definiuje typ, więc brak klucza w innym języku nie przejdzie
 * kompilacji. Panel jest narzędziem wewnętrznym Firmeto — na start polski i angielski;
 * kolejne języki to kolejne wpisy w `dictionaries`, nie zmiana kodu.
 */
export const locales = ['pl', 'en'] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = 'pl';
export const localeNames: Record<Locale, string> = { pl: 'Polski', en: 'English' };

const pl = {
  appName: 'Firmeto',
  panel: 'Panel operatora',
  role: 'operator',
  loading: 'Wczytuję…',
  language: 'Język',
  density: 'Gęstość',
  comfortable: 'Komfortowa',
  dense: 'Gęsta',
  signOut: 'Wyloguj',
  navigation: 'Nawigacja',
  entry: {
    title: 'Panel operatora Firmeto',
    lead: 'Kolejka kredytów do zatwierdzenia, karty firm, wzorce i cenniki. Wejście tylko dla operatorów: konto zakłada administrator, a drugi składnik jest obowiązkowy od pierwszego logowania.',
    signIn: 'Zaloguj się do panelu',
    paneHint: 'Po zalogowaniu wylądujesz w kolejce kredytów do zatwierdzenia.',
    sessionLost: 'Sesja wygasła — zaloguj się ponownie.',
  },
  groups: { billing: 'Rozliczenia', clients: 'Klienci', config: 'Konfiguracja' },
  credits: {
    open: 'Kredyty do zatwierdzenia',
    title: 'Kredyty do zatwierdzenia',
    lead: 'Korekta przychodu w dół w rozliczonym miesiącu zmniejsza opłatę — różnica czeka tu na decyzję, żeby cofanie faktur korektami nie było sposobem na odzyskanie pieniędzy bez kontroli.',
    empty: 'Nic nie czeka na decyzję.',
    company: 'Firma',
    office: 'biuro — suma za firmy z pakietu',
    period: 'Miesiąc',
    revenue: 'Przychód: było → jest',
    fee: 'Opłata: było → jest',
    amount: 'Kredyt',
    waiting: 'Czeka od',
    approve: 'Zatwierdź',
    reject: 'Odrzuć',
    note: 'Uzasadnienie',
    noteHint: 'Odmowa wymaga uzasadnienia; przy zatwierdzeniu też warto zapisać, dlaczego — za rok nikt tego nie odtworzy.',
    decided: 'Rozpatrzone.',
    card: 'Karta firmy',
  },
  companies: {
    open: 'Firmy',
    title: 'Firmy',
    lead: 'Wszystkie firmy; NIP albo fragment nazwy zawęża listę. Widzisz firmę z zewnątrz — kto ją prowadzi i jak się rozlicza — bez ksiąg i dokumentów.',
    search: 'NIP albo nazwa',
    none: 'Żadna firma nie pasuje.',
    count: (ile: number) => (ile === 1 ? '1 firma' : `${ile} firm`),
    name: 'Nazwa',
    nip: 'NIP',
    form: 'Forma',
    city: 'Miasto',
    owners: 'Właściciele',
    settlement: 'Rozliczenie',
    archived: 'zarchiwizowana',
    forms: {
      JDG: 'JDG',
      SP_CYWILNA: 's.c.',
      SP_JAWNA: 'sp.j.',
      SP_PARTNERSKA: 'sp.p.',
      SP_KOMANDYTOWA: 'sp.k.',
      SP_KOMANDYTOWO_AKCYJNA: 'S.K.A.',
      SP_ZOO: 'sp. z o.o.',
      PROSTA_SA: 'P.S.A.',
      SA: 'S.A.',
      SPOLDZIELNIA: 'spółdzielnia',
      STOWARZYSZENIE: 'stowarzyszenie',
      FUNDACJA: 'fundacja',
    } as Record<string, string>,
    taxation: { RYCZALT: 'ryczałt', SKALA: 'skala', LINIOWY: 'liniowy', CIT: 'CIT' } as Record<string, string>,
    books: { RYCZALT: 'ewidencja', KPIR: 'KPiR', FULL: 'pełne księgi' } as Record<string, string>,
    vat: { ACTIVE_MONTHLY: 'VAT miesięcznie', ACTIVE_QUARTERLY: 'VAT kwartalnie', EXEMPT: 'zwolniona z VAT' } as Record<string, string>,
    noSettings: 'bez ustawień',
  },
  card: {
    back: 'Wróć do listy',
    since: 'W Firmeto od',
    people: 'Osoby',
    role: 'Rola',
    from: 'Od',
    to: 'Do',
    stillActive: 'nadal',
    months: 'Rozliczenia',
    noMonths: 'Jeszcze nic nie naliczono.',
    plan: 'Plan',
    payer: 'Płaci',
    revenue: 'Przychód',
    fee: 'Opłata',
    zero: 'Zero, bo',
    adjustments: 'Korekty',
    noAdjustments: 'Brak korekt.',
    kind: 'Rodzaj',
    status: 'Stan',
    amount: 'Kwota',
    note: 'Uzasadnienie',
    manual: 'Ręczna korekta',
    manualLead: 'Ujemna kwota to kredyt — zatwierdzony od razu, bo to Ty go przyznajesz. Dodatnia to dopłata do najbliższego obciążenia. Uzasadnienie jest obowiązkowe: za rok nikt nie odtworzy, dlaczego firma dostała 40 zł, jeśli nie zapisano tego teraz.',
    period: 'Miesiąc (RRRR-MM)',
    save: 'Zapisz korektę',
    saved: 'Korekta zapisana.',
    kinds: { SURCHARGE: 'dopłata', CREDIT: 'kredyt' } as Record<string, string>,
    statuses: {
      PENDING_CHARGE: 'do obciążenia',
      CHARGED: 'obciążona',
      PENDING_APPROVAL: 'czeka na decyzję',
      APPROVED: 'zatwierdzona',
      REJECTED: 'odrzucona',
    } as Record<string, string>,
    roles: {
      OWNER: 'właściciel',
      ADMIN: 'administrator',
      ACCOUNTANT: 'księgowa',
      EMPLOYEE: 'pracownik',
      BOARD_MEMBER: 'członek zarządu',
      VIEWER: 'podgląd',
    } as Record<string, string>,
    zeroReasons: { TRIAL: 'okres próbny', NO_REVENUE: 'brak przychodu', BELOW_MINIMUM: 'poniżej progu' } as Record<string, string>,
    office: 'Biuro rachunkowe',
    noOffice: 'Firma prowadzi księgi sama — bez biura.',
    openOffice: 'Karta biura',
    ofWhichAgreement: 'w tym umowa:',
  },
  agreements: {
    title: 'Umowa indywidualna',
    leadCompany: 'Warunki ponad cennik: wyższy limit dokumentów tworzonych przez API (w cenie jest 1 000 miesięcznie) i opłata za niego. Opłata dolicza się do planu, mieści się pod sufitem 20% przychodu i nie dostaje zniżki wielofirmowej.',
    leadOffice: 'Warunki ponad cennik: sufit pakietu indywidualnego oraz limit i opłata za dokumenty, które pracownicy biura tworzą przez API i MCP. Biuro nie ma darmowej puli — bez limitu w umowie jego pracownicy nie utworzą przez API żadnego dokumentu. Opłata dolicza się ponad sufit.',
    none: 'Brak — obowiązuje cennik.',
    from: 'Od',
    to: 'Do',
    fee: 'Opłata netto / mies.',
    limit: 'Limit dokumentów API / mies.',
    ceiling: 'Sufit pakietu brutto',
    reference: 'Numer umowy lub warunki',
    open: 'bezterminowo',
    standard: 'z cennika',
    noApi: 'bez API',
    add: 'Nowa umowa',
    addLead: 'Obowiązuje pełnymi miesiącami, od bieżącego miesiąca. Warunków obowiązującej umowy się nie zmienia — kończy się ją i zakłada nową.',
    save: 'Zapisz umowę',
    saved: 'Umowa zapisana.',
    end: 'Zakończ z końcem miesiąca',
    endLabel: 'Ostatni miesiąc (RRRR-MM)',
    cancel: 'Usuń — jeszcze nie obowiązuje',
    ended: 'Umowa zakończona.',
    cancelled: 'Umowa usunięta.',
  },
  cycle: {
    open: 'Obciążenia',
    title: 'Obciążenia i zaległości',
    lead: 'Piątego dnia miesiąca naliczamy poprzedni miesiąc i obciążamy płatników, a codziennie ponawiamy zaległe obciążenia co kilka dni. Po dwóch tygodniach zaległości płatnik jest tylko do odczytu, aż zapłaci — bez Twojego udziału.',
    arrears: 'Zaległości',
    noArrears: 'Nikt nie zalega.',
    payer: 'Płatnik',
    office: 'biuro',
    period: 'Miesiąc',
    amount: 'Kwota',
    reason: 'Powód',
    attempts: 'Próby',
    readOnlyFrom: 'Tylko do odczytu od',
    readOnlyNow: 'tylko do odczytu',
    retry: 'Ponów teraz',
    postpone: 'Przesuń termin',
    postponeTo: 'Nowy termin',
    note: 'Uzasadnienie',
    save: 'Zapisz',
    reasons: { mandate_missing: 'brak zgody na obciążanie', card_declined: 'odmowa operatora', declined: 'odmowa operatora' } as Record<string, string>,
    runs: 'Przebiegi',
    noRuns: 'Zadanie jeszcze się nie uruchomiło.',
    job: 'Zadanie',
    jobs: { monthly: 'rozliczenie miesiąca', retry: 'ponowienia' } as Record<string, string>,
    key: 'Za',
    started: 'Start',
    settled: 'Naliczono',
    charged: 'Pobrano',
    failed: 'Nie udało się',
    running: 'trwa',
    runMonth: 'Rozlicz miesiąc ręcznie',
    runMonthLead: 'Np. po awarii w dniu rozliczenia. Miesiąc rozliczony wcześniej nie liczy się drugi raz.',
    run: 'Rozlicz',
    done: 'Gotowe.',
  },
  providers: {
    open: 'Operatorzy płatności',
    title: 'Operatorzy płatności',
    lead: 'Kto obsługuje karty i BLIK, od kiedy i za ile. Domyślny dla klienta jest najtańszy dla nas operator przy kwocie, jaką klient zwykle płaci; klient może wybrać innego z dopuszczonych. Reguły dotyczą nowych zgód — zgody wydane wcześniej działają dalej. Forma płatności bez żadnej reguły dopuszcza każdego operatora z integracją.',
    rules: 'Reguły',
    none: 'Brak reguł — dopuszczony jest każdy operator z integracją.',
    provider: 'Operator',
    method: 'Forma płatności',
    methods: { CARD: 'karta', BLIK: 'BLIK', TRANSFER: 'przelew' } as Record<string, string>,
    from: 'Od',
    to: 'Do',
    fee: 'Koszt',
    note: 'Uwagi',
    active: 'dopuszczony',
    inactive: 'wyłączony',
    disable: 'Wyłącz teraz',
    enable: 'Włącz',
    add: 'Nowa reguła',
    addLead: 'Zmiana kosztu to nowa reguła od dnia zmiany i zakończenie starej — wstecz nie zmieniamy, kto był dopuszczony.',
    percent: 'Procent',
    fixed: 'Opłata stała (zł)',
    save: 'Zapisz regułę',
    saved: 'Zapisano.',
    preview: 'Kto byłby domyślny',
    amount: 'Kwota (zł)',
    check: 'Sprawdź',
    cost: 'Koszt dla nas',
    isDefault: 'domyślny',
    nobody: 'Nikt nie jest dopuszczony dla tej formy płatności.',
  },
  pricing: {
    open: 'Cenniki',
    title: 'Cenniki',
    lead: 'Miesiąc rozlicza się po cenniku obowiązującym w jego ostatnim dniu, więc cennik, który już obowiązuje, jest niezmienny — zmiana to nowa wersja od przyszłego miesiąca. Cena planu nie zapisze się powyżej ceny iFirma: to blokada, nie uwaga.',
    current: 'Obowiązujący cennik',
    versions: 'Wersje',
    validFrom: 'Obowiązuje od',
    status: { CURRENT: 'obowiązuje', FUTURE: 'wejdzie', PAST: 'archiwalny' } as Record<string, string>,
    note: 'Uzasadnienie',
    noteHint: 'Dlaczego cennik się zmienia — za rok nikt tego nie odtworzy.',
    plan: 'Plan',
    companyNet: 'Firma płaci sama (netto)',
    companyGross: 'brutto',
    officeNet: 'W pakiecie biura (netto)',
    storeGross: 'Sklep (brutto)',
    reference: 'iFirma (netto)',
    noReference: 'brak odniesienia',
    headroom: 'zapas',
    aboveReference: 'powyżej iFirma — nie zapisze się',
    parameters: 'Parametry',
    vatRate: 'Stawka VAT',
    minimumFee: 'Próg minimalny (brutto)',
    revenueCap: 'Udział w przychodzie',
    trialMonths: 'Miesiące próbne',
    packages: 'Sufity pakietów biur (brutto)',
    newVersion: 'Nowa wersja',
    newVersionLead: 'Na podstawie wybranej wersji; wejdzie pierwszego dnia wskazanego miesiąca, najwcześniej od przyszłego.',
    edit: 'Popraw',
    remove: 'Usuń',
    removeConfirm: 'Usunąć tę wersję cennika? Jeszcze nie obowiązuje, więc nic nie było po niej naliczone.',
    save: 'Zapisz cennik',
    saveChanges: 'Zapisz poprawki',
    cancel: 'Anuluj',
    saved: 'Zapisano.',
    preview: 'Ile zapłaci firma',
    previewLead: 'Ten sam kalkulator, którym nalicza się miesiące: mniejsza z ceny planu i udziału w przychodzie, zero poniżej progu.',
    revenue: 'Przychód miesiąca (netto)',
    payer: 'Płatnik',
    planPrice: 'Cena planu',
    cap: 'Udział w przychodzie',
    fee: 'Do zapłaty',
    zero: { TRIAL: 'okres próbny', NO_REVENUE: 'brak przychodu', BELOW_MINIMUM: 'poniżej progu', FREE_AFTER_LEAVING_OFFICE: 'bezpłatnie po odejściu z biura', STORE_SUBSCRIPTION: 'subskrypcja ze sklepu' } as Record<string, string>,
    references: 'Odniesienie iFirma',
    referencesLead: 'Dziennik obserwacji cen iFirma — najnowsza dla planu jest punktem odniesienia. Nie nadpisujemy, dopisujemy.',
    observedOn: 'Zaobserwowano',
    source: 'Źródło',
    addReference: 'Dopisz obserwację',
    referenceSaved: 'Obserwacja zapisana.',
    netMonthly: 'Cena netto / mies.',
  },
  taxOffices: {
    open: 'Urzędy skarbowe',
    title: 'Słownik urzędów skarbowych',
    lead: 'Kodów urzędów nie przepisujemy z żadnej strony: źródłem jest plik KodyUrzedowSkarbowych ze schemy MF — to samo wyliczenie, którym bramka sprawdza KodUrzedu w JPK. Wgraj plik, obejrzyj różnicę, zatwierdź. Zastosować da się tylko wersję, którą importuje nasza schema JPK; nowa wersja wchodzi razem z nową schemą, czyli zmianą w kodzie.',
    pinned: 'Wersja w schemie JPK',
    inDb: 'Wersja w bazie',
    consistent: 'zgodne',
    inconsistent: 'niezgodne — zastosuj plik w wersji ze schemy JPK',
    active: 'Urzędów do wyboru',
    specialised: 'w tym wyspecjalizowanych',
    inactive: 'wyłączonych (zlikwidowane, połączone)',
    upload: 'Wgraj plik ze schemy MF',
    uploadHint: 'KodyUrzedowSkarbowych_v8-0E.xsd z crd.gov.pl — nazwa pliku niesie wersję.',
    preview: 'Pokaż różnicę',
    previewTitle: 'Różnica wobec bazy',
    file: 'Plik',
    version: 'Wersja',
    added: 'Dodane',
    removed: 'Usunięte',
    changed: 'Zmienione nazwy',
    unchanged: 'Bez zmian',
    noChanges: 'Plik zgadza się z bazą co do kodu i nazwy — nie ma czego zatwierdzać.',
    versionMismatch: (file: string, pinned: string) => `Plik jest w wersji ${file}, a nasza schema JPK importuje ${pinned}. Różnicę możesz obejrzeć, ale zastosować — nie: nowa wersja słownika wchodzi razem z nową schemą JPK (tools/urzedy/README.md).`,
    apply: 'Zatwierdź i zastosuj',
    applied: 'Słownik zaktualizowany.',
    cancel: 'Odrzuć podgląd',
    code: 'Kod',
    name: 'Nazwa',
    before: 'Było',
    after: 'Będzie',
    specialisedFlag: 'wyspecjalizowany',
    inactiveFlag: 'wyłączony',
    history: 'Historia wgrań',
    noHistory: 'Nikt jeszcze nie wgrywał słownika z panelu — bieżący pochodzi z migracji.',
    uploadedAt: 'Wgrano',
    status: { PREVIEW: 'tylko podgląd', APPLIED: 'zastosowano' } as Record<string, string>,
    list: 'Słownik',
    search: 'Kod albo fragment nazwy',
    count: (n: number) => (n === 1 ? '1 urząd' : n >= 2 && n <= 4 ? `${n} urzędy` : `${n} urzędów`),
    open_: 'Otwórz',
  },
  inboundMail: {
    open: 'Poczta z dokumentami',
    title: 'Odbiór dokumentów z poczty',
    lead: 'Adresy nazwa-KOD@dokumenty.firmeto.pl działają, gdy Mailgun zna domenę, przekazuje wiadomości do API i rekordy MX w DNS wskazują na Mailgun. Domenę i regułę API dodaje samo przy starcie tym samym kluczem, którym wysyła pocztę; rekordy MX trzeba wpisać u dostawcy DNS — tu widać, czy już są.',
    notConfigured: 'Bez klucza API Mailguna (MAILGUN_API_KEY) odbiór poczty nie jest ustawiany — to środowisko nie przyjmuje dokumentów z e-maila.',
    domain: 'Domena adresów',
    webhook: 'Adres, na który Mailgun przekazuje wiadomości',
    checkedAt: 'Ostatnie sprawdzenie',
    never: 'jeszcze nie sprawdzano',
    check: 'Sprawdź teraz',
    checking: 'Sprawdzam…',
    stateReady: 'Odbiór działa',
    stateWaiting: 'Odbiór nie działa — patrz niżej',
    domainKnown: 'Domena w Mailgunie',
    routeReady: 'Reguła przekazująca do API',
    mxValid: 'Rekordy MX w DNS',
    yes: 'jest',
    no: 'brak',
    problem: 'Mailgun odpowiedział błędem',
    records: 'Rekordy do wpisania u dostawcy DNS',
    recordsHint: 'Dla domeny poniżej, typ MX, priorytet 10. Po dodaniu zmiany w DNS rozchodzą się do kilku godzin — potem „Sprawdź teraz”.',
    recordType: 'Typ',
    recordPriority: 'Priorytet',
    recordValue: 'Wartość',
    recordState: 'Stan',
    valid: 'widoczny',
    invalid: 'brak',
  },
  offices: {
    open: 'Biura rachunkowe',
    title: 'Biura rachunkowe',
    lead: 'Biuro widziane z zewnątrz: zespół, obsługiwane firmy i to, za które płaci ze swojego pakietu. Umowa obsługi to sprawa biura i firmy — tu się ją ogląda, nie zmienia.',
    search: 'NIP albo nazwa biura',
    count: (n: number) => (n === 1 ? '1 biuro' : n >= 2 && n <= 4 ? `${n} biura` : `${n} biur`),
    none: 'Żadne biuro nie pasuje.',
    name: 'Biuro',
    nip: 'NIP',
    package: 'Pakiet',
    members: 'Zespół',
    clients: 'Firmy',
    paidByOffice: 'Płaci biuro',
    pending: 'Czeka na zgodę',
    since: 'w Firmeto od',
    back: 'Wróć do listy',
    team: 'Zespół biura',
    role: 'Rola',
    from: 'Od',
    to: 'Do',
    stillActive: 'nadal',
    agreements: 'Obsługiwane firmy',
    company: 'Firma',
    payer: 'Płatnik',
    status: 'Stan umowy',
    accountants: 'Dostęp do ksiąg',
    noAgreements: 'Biuro nie obsługuje jeszcze żadnej firmy.',
    months: 'Miesiące opłacane przez biuro',
    monthsLead: 'Suma naliczeń za firmy, za które w danym miesiącu płaciło biuro, sufit pakietu (z umowy indywidualnej albo z cennika) i opłata z umowy, która dolicza się ponad sufit.',
    noMonths: 'Nic jeszcze nie naliczono na rachunek biura.',
    companiesCount: 'Firm',
    revenue: 'Przychód firm',
    fee: 'Naliczono',
    statuses: {
      PENDING: 'czeka na zgodę firmy',
      EFFECTIVE: 'obowiązuje',
      REJECTED: 'odrzucona przez firmę',
      ENDED: 'zakończona',
    } as Record<string, string>,
    roles: {
      OFFICE_OWNER: 'właściciel biura',
      OFFICE_ADMIN: 'administrator biura',
      ACCOUNTANT: 'księgowa',
      ASSISTANT: 'asystent',
    } as Record<string, string>,
    packages: { biuro_10: 'Biuro 10', biuro_50: 'Biuro 50', biuro_200: 'Biuro 200', indywidualny: 'indywidualny' } as Record<string, string>,
    payers: { COMPANY: 'firma', OFFICE: 'biuro' } as Record<string, string>,
    ceiling: 'Sufit',
    charged: 'Do zapłaty',
    agreementFee: 'Umowa',
  },
  dict: {
    roles: {
      RECEIVABLES: 'Należności od odbiorców',
      PAYABLES: 'Zobowiązania wobec dostawców',
      VAT_DUE: 'VAT należny',
      VAT_INPUT: 'VAT naliczony',
      VAT_SETTLEMENT: 'Rozliczenie VAT',
      SALES_SERVICES: 'Sprzedaż usług',
      SALES_GOODS: 'Sprzedaż towarów',
      COST_OF_GOODS: 'Wartość sprzedanych towarów',
      GOODS: 'Towary',
      COSTS_MATERIALS: 'Zużycie materiałów i energii',
      COSTS_SERVICES: 'Usługi obce',
      COSTS_TAXES: 'Podatki i opłaty',
      COSTS_SALARIES: 'Koszty wynagrodzeń',
      COSTS_SOCIAL: 'Ubezpieczenia społeczne i inne świadczenia',
      COSTS_OTHER: 'Pozostałe koszty rodzajowe',
      CASH: 'Kasa',
      BANK: 'Rachunek bankowy',
      INCOME_TAX: 'Podatek dochodowy',
      SOCIAL_SECURITY: 'Rozrachunki z ZUS',
      PAYROLL: 'Rozrachunki z pracownikami',
      RESULT: 'Wynik finansowy',
      PRIOR_YEARS_RESULT: 'Wynik lat ubiegłych',
    } as Record<string, string>,
    kinds: { BALANCE: 'bilansowe', RESULT: 'wynikowe', OFFBALANCE: 'pozabilansowe' } as Record<string, string>,
    sides: { DEBIT: 'aktywne (Wn)', CREDIT: 'pasywne (Ma)', BOTH: 'aktywno-pasywne', NONE: 'bez strony' } as Record<string, string>,
    tax: {
      DEDUCTIBLE: 'koszt podatkowy',
      NON_DEDUCTIBLE: 'koszt niepodatkowy (NKUP)',
      TAXABLE: 'przychód podatkowy',
      NON_TAXABLE: 'przychód niepodatkowy',
    } as Record<string, string>,
    statementTemplates: {
      ZAL_1: 'Załącznik 1 — jednostka pełna',
      ZAL_4: 'Załącznik 4 — jednostka mikro',
      ZAL_5: 'Załącznik 5 — jednostka mała',
    } as Record<string, string>,
    costVariants: {
      BY_KIND: 'wariant porównawczy',
      BY_FUNCTION: 'wariant kalkulacyjny',
      BOTH: 'oba warianty',
    } as Record<string, string>,
    sections: { ASSETS: 'Aktywa', LIABILITIES: 'Pasywa', INCOME: 'Rachunek zysków i strat' } as Record<string, string>,
    schemeKinds: { INVOICE: 'Faktura sprzedaży', CORRECTION: 'Korekta sprzedaży', COST: 'Dokument kosztowy' } as Record<string, string>,
    sources: {
      GROSS: 'brutto',
      NET: 'netto',
      VAT: 'VAT',
      VAT_DEDUCTIBLE: 'VAT do odliczenia',
      COST_AMOUNT: 'kwota kosztu (netto + VAT nieodliczony)',
    } as Record<string, string>,
    entrySides: { DEBIT: 'Wn', CREDIT: 'Ma' } as Record<string, string>,
    counterpartyTypes: { DOMESTIC: 'krajowy', EU: 'z Unii', NON_EU: 'poza Unią', PERSON: 'osoba prywatna' } as Record<string, string>,
    costCategories: {
      GOODS: 'towary i materiały',
      PURCHASE_COSTS: 'koszty uboczne zakupu',
      SALARIES: 'wynagrodzenia',
      OTHER: 'pozostałe wydatki',
    } as Record<string, string>,
    plans: { RYCZALT: 'Ryczałt', KPIR: 'KPiR', FULL: 'Pełna księgowość' } as Record<string, string>,
  },
  templates: {
    open: 'Wzorce planu kont',
    title: 'Wzorce planu kont',
    lead: 'Z wzorca firma kopiuje plan kont na rok obrotowy. Zmiana wzorca nie rusza planów już założonych — one są kopiami. Licznik użyć mówi, ile firm zaczęło od danego wzorca.',
    none: 'Nie ma jeszcze żadnego wzorca.',
    new: 'Nowy wzorzec',
    newLead: 'Pusty albo jako kopia innego wzorca. Kopia jest punktem wyjścia, nie powiązaniem: późniejsze zmiany źródła nie przechodzą dalej.',
    copyFrom: 'Skopiuj konta z',
    empty: '— pusty wzorzec —',
    name: 'Nazwa',
    description: 'Opis',
    statementTemplate: 'Załącznik sprawozdania',
    costVariant: 'Rachunek kosztów',
    accounts: 'Konta',
    usages: 'Użyć',
    inactive: 'wyłączony',
    activeLabel: 'Aktywny — firmy widzą go przy zakładaniu roku',
    save: 'Zapisz',
    saved: 'Zapisano.',
    create: 'Załóż wzorzec',
    cancel: 'Anuluj',
    back: 'Wróć do listy',
    meta: 'Opis wzorca',
    usageCount: (n: number) => (n === 1 ? '1 plan kont powstał z tego wzorca' : `${n} planów kont powstało z tego wzorca`),
    editAccounts: 'Edytuj konta',
    addAccount: 'Dodaj konto',
    edit: 'Zmień',
    remove: 'Usuń',
    apply: 'Zastosuj',
    number: 'Numer',
    accountName: 'Nazwa konta',
    kind: 'Rodzaj',
    side: 'Strona salda',
    settlement: 'rozrachunkowe',
    currency: 'walutowe',
    tax: 'Podatkowo',
    taxNone: '— nie dotyczy —',
    role: 'Rola w schematach',
    roleNone: '— bez roli —',
    positionDebit: 'Pozycja sprawozdania (saldo Wn)',
    positionCredit: 'Pozycja sprawozdania (saldo Ma)',
    positionNone: '— bez pozycji —',
    position: 'Pozycja',
    saveAccounts: 'Zapisz komplet kont',
    accountsHint: 'Zapisujemy całą listę naraz. Konto wzorca przechodzi te same reguły, co konto planu — inaczej firma nie mogłaby założyć roku.',
    discard: 'Porzuć zmiany',
    unsaved: 'niezapisane zmiany',
    numberHint: 'Trzycyfrowa syntetyka i analityka po myślniku, np. 201-1. Analityka wymaga syntetyki na liście.',
    unmapped: (n: number) => `${n} kont bilansowych i wynikowych bez pozycji sprawozdania — ich salda trafią do listy „nieprzypisane” zamiast do bilansu.`,
  },
  schemes: {
    open: 'Schematy księgowania',
    title: 'Schematy księgowania',
    lead: 'Schemat wzorcowy działa u wszystkich firm, więc konto wskazuje rolą, nie numerem. Zmiana to nowa wersja od wskazanej daty — zapisy już zaksięgowane zostają, jak były.',
    none: 'Nie ma jeszcze żadnego schematu.',
    new: 'Nowy schemat',
    newTitle: 'Nowy schemat wzorcowy',
    name: 'Nazwa',
    kind: 'Dokument',
    condition: 'Warunek',
    counterpartyType: 'Kontrahent',
    anyCounterparty: 'dowolny',
    costCategory: 'Kategoria kosztu',
    anyCategory: 'dowolna',
    validity: 'Obowiązuje',
    validFrom: 'Obowiązuje od',
    validTo: 'Obowiązuje do',
    openEnded: 'bez końca',
    lines: 'Wiersze dekretu',
    role: 'Konto (rola)',
    side: 'Strona',
    source: 'Kwota',
    withCounterparty: 'z kontrahentem',
    description: 'Opis wiersza',
    addLine: 'Dodaj wiersz',
    removeLine: 'Usuń wiersz',
    preview: 'Podgląd dekretu',
    previewLead: 'Ten sam kod, którym księguje aplikacja — na kontach wybranego wzorca i przykładowych kwotach.',
    onTemplate: 'Na wzorcu planu kont',
    sampleNet: 'Netto',
    sampleVat: 'VAT',
    sampleDeductible: 'VAT do odliczenia',
    sampleCounterparty: 'Dokument ma kontrahenta',
    account: 'Konto',
    amount: 'Kwota',
    debit: 'Wn',
    credit: 'Ma',
    total: 'Razem',
    postable: 'Dekret się bilansuje i przejdzie przez dziennik.',
    notPostable: 'Dekret nie przejdzie:',
    previewEmpty: 'Uzupełnij wiersze, żeby zobaczyć dekret.',
    save: 'Zapisz schemat',
    saveVersion: 'Zapisz nową wersję',
    newVersion: 'Nowa wersja od daty',
    versionLead: 'Poprzednia wersja skończy się dzień wcześniej. Dokumenty z wcześniejszą datą księgują się dalej po staremu.',
    rename: 'Nazwa i widoczność',
    activeLabel: 'Aktywny — aplikacja proponuje ten schemat',
    inactive: 'wyłączony',
    closed: 'zakończony',
    future: 'od',
    current: 'obowiązuje',
    saved: 'Zapisano.',
    back: 'Wróć do listy',
    cancel: 'Anuluj',
    settlementHint: 'Konto rozrachunkowe zawsze niesie kontrahenta — bez niego zapis nie przejdzie przez dziennik.',
  },
};

export type Dictionary = typeof pl;

const en: Dictionary = {
  appName: 'Firmeto',
  panel: 'Operator panel',
  role: 'operator',
  loading: 'Loading…',
  language: 'Language',
  density: 'Density',
  comfortable: 'Comfortable',
  dense: 'Dense',
  signOut: 'Sign out',
  navigation: 'Navigation',
  entry: {
    title: 'Firmeto operator panel',
    lead: 'Credit approval queue, company cards, templates and price lists. Operators only: accounts are created by an administrator and a second factor is mandatory from the first sign-in.',
    signIn: 'Sign in to the panel',
    paneHint: 'After signing in you land in the credit approval queue.',
    sessionLost: 'Your session has expired — sign in again.',
  },
  groups: { billing: 'Billing', clients: 'Clients', config: 'Configuration' },
  credits: {
    open: 'Credits awaiting approval',
    title: 'Credits awaiting approval',
    lead: 'A downward revenue correction in a settled month lowers the fee — the difference waits here for a decision, so that reversing invoices with corrections is not a way to get money back without oversight.',
    empty: 'Nothing is waiting for a decision.',
    company: 'Company',
    office: 'office — total for the companies in its package',
    period: 'Month',
    revenue: 'Revenue: was → is',
    fee: 'Fee: was → is',
    amount: 'Credit',
    waiting: 'Waiting since',
    approve: 'Approve',
    reject: 'Reject',
    note: 'Justification',
    noteHint: 'A rejection requires a justification; on approval it is worth writing down why as well — nobody will reconstruct it a year from now.',
    decided: 'Decided.',
    card: 'Company card',
  },
  companies: {
    open: 'Companies',
    title: 'Companies',
    lead: 'All companies; a tax ID or part of the name narrows the list. You see the company from the outside — who runs it and how it settles — without books or documents.',
    search: 'Tax ID or name',
    none: 'No company matches.',
    count: (ile: number) => (ile === 1 ? '1 company' : `${ile} companies`),
    name: 'Name',
    nip: 'Tax ID',
    form: 'Form',
    city: 'City',
    owners: 'Owners',
    settlement: 'Settlement',
    archived: 'archived',
    forms: {
      JDG: 'sole trader',
      SP_CYWILNA: 'civil partnership',
      SP_JAWNA: 'general partnership',
      SP_PARTNERSKA: 'professional partnership',
      SP_KOMANDYTOWA: 'limited partnership',
      SP_KOMANDYTOWO_AKCYJNA: 'limited joint-stock partnership',
      SP_ZOO: 'Ltd.',
      PROSTA_SA: 'simple joint-stock company',
      SA: 'joint-stock company',
      SPOLDZIELNIA: 'cooperative',
      STOWARZYSZENIE: 'association',
      FUNDACJA: 'foundation',
    },
    taxation: { RYCZALT: 'lump sum', SKALA: 'progressive', LINIOWY: 'flat', CIT: 'CIT' },
    books: { RYCZALT: 'revenue ledger', KPIR: 'KPiR', FULL: 'full accounting' },
    vat: { ACTIVE_MONTHLY: 'VAT monthly', ACTIVE_QUARTERLY: 'VAT quarterly', EXEMPT: 'VAT exempt' },
    noSettings: 'no settings',
  },
  card: {
    back: 'Back to the list',
    since: 'With Firmeto since',
    people: 'People',
    role: 'Role',
    from: 'From',
    to: 'To',
    stillActive: 'ongoing',
    months: 'Billing',
    noMonths: 'Nothing has been charged yet.',
    plan: 'Plan',
    payer: 'Paid by',
    revenue: 'Revenue',
    fee: 'Fee',
    zero: 'Zero because',
    adjustments: 'Adjustments',
    noAdjustments: 'No adjustments.',
    kind: 'Kind',
    status: 'Status',
    amount: 'Amount',
    note: 'Justification',
    manual: 'Manual adjustment',
    manualLead: 'A negative amount is a credit — approved immediately, because you are granting it. A positive one is a surcharge added to the next charge. A justification is mandatory: a year from now nobody will reconstruct why the company received PLN 40 unless it is written down now.',
    period: 'Month (YYYY-MM)',
    save: 'Save adjustment',
    saved: 'Adjustment saved.',
    kinds: { SURCHARGE: 'surcharge', CREDIT: 'credit' },
    statuses: {
      PENDING_CHARGE: 'to be charged',
      CHARGED: 'charged',
      PENDING_APPROVAL: 'awaiting decision',
      APPROVED: 'approved',
      REJECTED: 'rejected',
    },
    roles: {
      OWNER: 'owner',
      ADMIN: 'administrator',
      ACCOUNTANT: 'accountant',
      EMPLOYEE: 'employee',
      BOARD_MEMBER: 'board member',
      VIEWER: 'viewer',
    },
    zeroReasons: { TRIAL: 'trial period', NO_REVENUE: 'no revenue', BELOW_MINIMUM: 'below the minimum' },
    office: 'Accounting office',
    noOffice: 'The company keeps its own books — no office.',
    openOffice: 'Office card',
    ofWhichAgreement: 'of which agreement:',
  },
  agreements: {
    title: 'Individual agreement',
    leadCompany: 'Terms beyond the price list: a higher limit of documents created through the API (1,000 a month are included) and its fee. The fee is added to the plan, stays under the 20% revenue cap and gets no multi-company discount.',
    leadOffice: 'Terms beyond the price list: the individual package ceiling, and the limit and fee for documents office staff create through the API and MCP. Offices get no free allowance — without a limit in the agreement their staff cannot create any document through the API. The fee is added on top of the ceiling.',
    none: 'None — the price list applies.',
    from: 'From',
    to: 'To',
    fee: 'Net fee / month',
    limit: 'API document limit / month',
    ceiling: 'Package ceiling, gross',
    reference: 'Agreement number or terms',
    open: 'open-ended',
    standard: 'from the price list',
    noApi: 'no API',
    add: 'New agreement',
    addLead: 'Applies in whole months, from the current month on. The terms of an agreement in force do not change — end it and create a new one.',
    save: 'Save agreement',
    saved: 'Agreement saved.',
    end: 'End at the end of a month',
    endLabel: 'Last month (YYYY-MM)',
    cancel: 'Delete — not in force yet',
    ended: 'Agreement ended.',
    cancelled: 'Agreement deleted.',
  },
  cycle: {
    open: 'Charges',
    title: 'Charges and arrears',
    lead: 'On the fifth of each month we settle the previous month and charge payers; every day we retry outstanding charges every few days. After two weeks in arrears a payer is read-only until they pay — without your involvement.',
    arrears: 'Arrears',
    noArrears: 'Nobody is in arrears.',
    payer: 'Payer',
    office: 'office',
    period: 'Month',
    amount: 'Amount',
    reason: 'Reason',
    attempts: 'Attempts',
    readOnlyFrom: 'Read-only from',
    readOnlyNow: 'read-only',
    retry: 'Retry now',
    postpone: 'Postpone',
    postponeTo: 'New date',
    note: 'Justification',
    save: 'Save',
    reasons: { mandate_missing: 'no payment mandate', card_declined: 'declined by operator', declined: 'declined by operator' } as Record<string, string>,
    runs: 'Runs',
    noRuns: 'The job has not run yet.',
    job: 'Job',
    jobs: { monthly: 'monthly settlement', retry: 'retries' } as Record<string, string>,
    key: 'For',
    started: 'Started',
    settled: 'Settled',
    charged: 'Charged',
    failed: 'Failed',
    running: 'running',
    runMonth: 'Settle a month manually',
    runMonthLead: 'E.g. after an outage on settlement day. A month settled before is not counted twice.',
    run: 'Settle',
    done: 'Done.',
  },
  providers: {
    open: 'Payment providers',
    title: 'Payment providers',
    lead: 'Who handles cards and BLIK, from when and at what cost. The customer default is the provider cheapest for us at the amount the customer usually pays; the customer can choose another allowed one. Rules apply to new mandates — mandates given earlier keep working. A payment method without any rule allows every integrated provider.',
    rules: 'Rules',
    none: 'No rules — every integrated provider is allowed.',
    provider: 'Provider',
    method: 'Payment method',
    methods: { CARD: 'card', BLIK: 'BLIK', TRANSFER: 'transfer' } as Record<string, string>,
    from: 'From',
    to: 'To',
    fee: 'Cost',
    note: 'Notes',
    active: 'allowed',
    inactive: 'disabled',
    disable: 'Disable now',
    enable: 'Enable',
    add: 'New rule',
    addLead: 'A cost change is a new rule from the day of the change and the end of the old one — we do not change retroactively who was allowed.',
    percent: 'Percent',
    fixed: 'Fixed fee (PLN)',
    save: 'Save rule',
    saved: 'Saved.',
    preview: 'Who would be the default',
    amount: 'Amount (PLN)',
    check: 'Check',
    cost: 'Cost for us',
    isDefault: 'default',
    nobody: 'No provider is allowed for this payment method.',
  },
  pricing: {
    open: 'Price lists',
    title: 'Price lists',
    lead: 'A month is billed by the price list in force on its last day, so a price list already in force is immutable — a change is a new version from a future month. A plan price above the iFirma price will not save: it is a block, not a warning.',
    current: 'Price list in force',
    versions: 'Versions',
    validFrom: 'Valid from',
    status: { CURRENT: 'in force', FUTURE: 'upcoming', PAST: 'archived' },
    note: 'Reason',
    noteHint: 'Why the price list changes — nobody will reconstruct it a year from now.',
    plan: 'Plan',
    companyNet: 'Company pays itself (net)',
    companyGross: 'gross',
    officeNet: 'In an office package (net)',
    storeGross: 'App store (gross)',
    reference: 'iFirma (net)',
    noReference: 'no reference',
    headroom: 'headroom',
    aboveReference: 'above iFirma — will not save',
    parameters: 'Parameters',
    vatRate: 'VAT rate',
    minimumFee: 'Minimum fee (gross)',
    revenueCap: 'Share of revenue',
    trialMonths: 'Trial months',
    packages: 'Office package caps (gross)',
    newVersion: 'New version',
    newVersionLead: 'Based on the selected version; takes effect on the first day of the chosen month, next month at the earliest.',
    edit: 'Edit',
    remove: 'Delete',
    removeConfirm: 'Delete this price list version? It is not in force yet, so nothing was billed by it.',
    save: 'Save price list',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    saved: 'Saved.',
    preview: 'What a company would pay',
    previewLead: 'The same calculator that bills the months: the lower of the plan price and the share of revenue, zero below the minimum.',
    revenue: 'Monthly revenue (net)',
    payer: 'Payer',
    planPrice: 'Plan price',
    cap: 'Share of revenue',
    fee: 'To pay',
    zero: { TRIAL: 'trial period', NO_REVENUE: 'no revenue', BELOW_MINIMUM: 'below the minimum', FREE_AFTER_LEAVING_OFFICE: 'free after leaving the office', STORE_SUBSCRIPTION: 'app store subscription' },
    references: 'iFirma reference',
    referencesLead: 'A log of observed iFirma prices — the latest per plan is the reference. Nothing is overwritten, only appended.',
    observedOn: 'Observed on',
    source: 'Source',
    addReference: 'Add observation',
    referenceSaved: 'Observation saved.',
    netMonthly: 'Net price / month',
  },
  taxOffices: {
    open: 'Tax offices',
    title: 'Tax office dictionary',
    lead: 'Tax office codes are never copied from a web page: the source is the KodyUrzedowSkarbowych file from the Ministry of Finance schema — the same enumeration the gateway validates KodUrzedu in JPK against. Upload the file, review the difference, approve. Only the version imported by our JPK schema can be applied; a new version arrives with a new schema, i.e. a code change.',
    pinned: 'Version in the JPK schema',
    inDb: 'Version in the database',
    consistent: 'consistent',
    inconsistent: 'inconsistent — apply the file in the JPK schema version',
    active: 'Offices available',
    specialised: 'of which specialised',
    inactive: 'disabled (closed, merged)',
    upload: 'Upload the MF schema file',
    uploadHint: 'KodyUrzedowSkarbowych_v8-0E.xsd from crd.gov.pl — the file name carries the version.',
    preview: 'Show the difference',
    previewTitle: 'Difference against the database',
    file: 'File',
    version: 'Version',
    added: 'Added',
    removed: 'Removed',
    changed: 'Renamed',
    unchanged: 'Unchanged',
    noChanges: 'The file matches the database by code and name — nothing to approve.',
    versionMismatch: (file: string, pinned: string) => `The file is version ${file}, while our JPK schema imports ${pinned}. You can review the difference but not apply it: a new dictionary version arrives with a new JPK schema (tools/urzedy/README.md).`,
    apply: 'Approve and apply',
    applied: 'Dictionary updated.',
    cancel: 'Discard the preview',
    code: 'Code',
    name: 'Name',
    before: 'Was',
    after: 'Will be',
    specialisedFlag: 'specialised',
    inactiveFlag: 'disabled',
    history: 'Upload history',
    noHistory: 'Nobody has uploaded the dictionary from the panel yet — the current one comes from a migration.',
    uploadedAt: 'Uploaded',
    status: { PREVIEW: 'preview only', APPLIED: 'applied' },
    list: 'Dictionary',
    search: 'Code or part of the name',
    count: (n: number) => (n === 1 ? '1 office' : `${n} offices`),
    open_: 'Open',
  },
  inboundMail: {
    open: 'Document e-mail',
    title: 'Receiving documents by e-mail',
    lead: 'Addresses name-CODE@dokumenty.firmeto.pl work once Mailgun knows the domain, forwards messages to the API and the MX records in DNS point to Mailgun. The API adds the domain and the route itself at start-up with the same key it uses to send mail; MX records have to be entered at the DNS provider — this page shows whether they are there.',
    notConfigured: 'Without a Mailgun API key (MAILGUN_API_KEY) inbound mail is not set up — this environment does not accept documents by e-mail.',
    domain: 'Address domain',
    webhook: 'Address Mailgun forwards messages to',
    checkedAt: 'Last check',
    never: 'not checked yet',
    check: 'Check now',
    checking: 'Checking…',
    stateReady: 'Receiving works',
    stateWaiting: 'Receiving does not work — see below',
    domainKnown: 'Domain in Mailgun',
    routeReady: 'Route forwarding to the API',
    mxValid: 'MX records in DNS',
    yes: 'present',
    no: 'missing',
    problem: 'Mailgun returned an error',
    records: 'Records to enter at the DNS provider',
    recordsHint: 'For the domain below, type MX, priority 10. DNS changes take up to a few hours to propagate — then “Check now”.',
    recordType: 'Type',
    recordPriority: 'Priority',
    recordValue: 'Value',
    recordState: 'State',
    valid: 'visible',
    invalid: 'missing',
  },
  offices: {
    open: 'Accounting offices',
    title: 'Accounting offices',
    lead: 'An office seen from the outside: its team, the companies it serves and the ones it pays for from its package. A service agreement is between the office and the company — here you look at it, not change it.',
    search: 'Tax ID or office name',
    count: (n: number) => (n === 1 ? '1 office' : `${n} offices`),
    none: 'No office matches.',
    name: 'Office',
    nip: 'Tax ID',
    package: 'Package',
    members: 'Team',
    clients: 'Companies',
    paidByOffice: 'Paid by office',
    pending: 'Awaiting consent',
    since: 'in Firmeto since',
    back: 'Back to the list',
    team: 'Office team',
    role: 'Role',
    from: 'From',
    to: 'To',
    stillActive: 'ongoing',
    agreements: 'Companies served',
    company: 'Company',
    payer: 'Payer',
    status: 'Agreement status',
    accountants: 'Access to the books',
    noAgreements: 'The office does not serve any company yet.',
    months: 'Months paid by the office',
    monthsLead: 'Sum of fees for companies the office paid for in a given month, the package ceiling (from the individual agreement or the price list) and the agreement fee, added on top of the ceiling.',
    noMonths: 'Nothing has been charged to the office yet.',
    companiesCount: 'Companies',
    revenue: 'Companies’ revenue',
    fee: 'Charged',
    statuses: {
      PENDING: 'awaiting the company’s consent',
      EFFECTIVE: 'in force',
      REJECTED: 'rejected by the company',
      ENDED: 'ended',
    },
    roles: {
      OFFICE_OWNER: 'office owner',
      OFFICE_ADMIN: 'office administrator',
      ACCOUNTANT: 'accountant',
      ASSISTANT: 'assistant',
    },
    packages: { biuro_10: 'Office 10', biuro_50: 'Office 50', biuro_200: 'Office 200', indywidualny: 'individual' },
    payers: { COMPANY: 'company', OFFICE: 'office' },
    ceiling: 'Cap',
    charged: 'To pay',
    agreementFee: 'Agreement',
  },
  dict: {
    roles: {
      RECEIVABLES: 'Trade receivables',
      PAYABLES: 'Trade payables',
      VAT_DUE: 'Output VAT',
      VAT_INPUT: 'Input VAT',
      VAT_SETTLEMENT: 'VAT settlement',
      SALES_SERVICES: 'Sales of services',
      SALES_GOODS: 'Sales of goods',
      COST_OF_GOODS: 'Cost of goods sold',
      GOODS: 'Goods',
      COSTS_MATERIALS: 'Materials and energy',
      COSTS_SERVICES: 'External services',
      COSTS_TAXES: 'Taxes and fees',
      COSTS_SALARIES: 'Salary costs',
      COSTS_SOCIAL: 'Social security and other benefits',
      COSTS_OTHER: 'Other costs by kind',
      CASH: 'Cash',
      BANK: 'Bank account',
      INCOME_TAX: 'Income tax',
      SOCIAL_SECURITY: 'Social insurance (ZUS)',
      PAYROLL: 'Payroll settlements',
      RESULT: 'Financial result',
      PRIOR_YEARS_RESULT: 'Prior years’ result',
    },
    kinds: { BALANCE: 'balance sheet', RESULT: 'profit and loss', OFFBALANCE: 'off-balance' },
    sides: { DEBIT: 'asset (Dr)', CREDIT: 'liability (Cr)', BOTH: 'asset or liability', NONE: 'no side' },
    tax: {
      DEDUCTIBLE: 'tax-deductible cost',
      NON_DEDUCTIBLE: 'non-deductible cost',
      TAXABLE: 'taxable revenue',
      NON_TAXABLE: 'non-taxable revenue',
    },
    statementTemplates: {
      ZAL_1: 'Appendix 1 — full entity',
      ZAL_4: 'Appendix 4 — micro entity',
      ZAL_5: 'Appendix 5 — small entity',
    },
    costVariants: {
      BY_KIND: 'by nature of expense',
      BY_FUNCTION: 'by function',
      BOTH: 'both variants',
    },
    sections: { ASSETS: 'Assets', LIABILITIES: 'Liabilities and equity', INCOME: 'Profit and loss' },
    schemeKinds: { INVOICE: 'Sales invoice', CORRECTION: 'Sales correction', COST: 'Cost document' },
    sources: {
      GROSS: 'gross',
      NET: 'net',
      VAT: 'VAT',
      VAT_DEDUCTIBLE: 'deductible VAT',
      COST_AMOUNT: 'cost amount (net + non-deductible VAT)',
    },
    entrySides: { DEBIT: 'Dr', CREDIT: 'Cr' },
    counterpartyTypes: { DOMESTIC: 'domestic', EU: 'EU', NON_EU: 'outside the EU', PERSON: 'private person' },
    costCategories: {
      GOODS: 'goods and materials',
      PURCHASE_COSTS: 'incidental purchase costs',
      SALARIES: 'salaries',
      OTHER: 'other expenses',
    },
    plans: { RYCZALT: 'Lump sum', KPIR: 'Revenue and expense ledger', FULL: 'Full accounting' },
  },
  templates: {
    open: 'Chart templates',
    title: 'Chart of accounts templates',
    lead: 'A company copies its chart of accounts for a fiscal year from a template. Changing a template does not touch charts already created — they are copies. The usage counter tells how many companies started from a template.',
    none: 'There are no templates yet.',
    new: 'New template',
    newLead: 'Empty or as a copy of another template. A copy is a starting point, not a link: later changes to the source do not propagate.',
    copyFrom: 'Copy accounts from',
    empty: '— empty template —',
    name: 'Name',
    description: 'Description',
    statementTemplate: 'Statement appendix',
    costVariant: 'Cost accounting',
    accounts: 'Accounts',
    usages: 'Uses',
    inactive: 'disabled',
    activeLabel: 'Active — companies see it when opening a year',
    save: 'Save',
    saved: 'Saved.',
    create: 'Create template',
    cancel: 'Cancel',
    back: 'Back to the list',
    meta: 'Template details',
    usageCount: (n: number) => (n === 1 ? '1 chart of accounts was created from this template' : `${n} charts of accounts were created from this template`),
    editAccounts: 'Edit accounts',
    addAccount: 'Add account',
    edit: 'Edit',
    remove: 'Remove',
    apply: 'Apply',
    number: 'Number',
    accountName: 'Account name',
    kind: 'Kind',
    side: 'Balance side',
    settlement: 'settlement',
    currency: 'foreign currency',
    tax: 'Tax treatment',
    taxNone: '— not applicable —',
    role: 'Role in schemes',
    roleNone: '— no role —',
    positionDebit: 'Statement position (debit balance)',
    positionCredit: 'Statement position (credit balance)',
    positionNone: '— no position —',
    position: 'Position',
    saveAccounts: 'Save all accounts',
    accountsHint: 'The whole list is saved at once. A template account passes the same rules as a chart account — otherwise a company could not open a year.',
    discard: 'Discard changes',
    unsaved: 'unsaved changes',
    numberHint: 'Three-digit synthetic account and sub-accounts after a dash, e.g. 201-1. A sub-account needs its synthetic account on the list.',
    unmapped: (n: number) => `${n} balance-sheet and P&L accounts without a statement position — their balances will land on the “unassigned” list instead of the balance sheet.`,
  },
  schemes: {
    open: 'Posting schemes',
    title: 'Posting schemes',
    lead: 'A system scheme works for every company, so it points to accounts by role, not by number. A change is a new version from a given date — entries already posted stay as they were.',
    none: 'There are no schemes yet.',
    new: 'New scheme',
    newTitle: 'New system scheme',
    name: 'Name',
    kind: 'Document',
    condition: 'Condition',
    counterpartyType: 'Counterparty',
    anyCounterparty: 'any',
    costCategory: 'Cost category',
    anyCategory: 'any',
    validity: 'Valid',
    validFrom: 'Valid from',
    validTo: 'Valid to',
    openEnded: 'open-ended',
    lines: 'Entry lines',
    role: 'Account (role)',
    side: 'Side',
    source: 'Amount',
    withCounterparty: 'with counterparty',
    description: 'Line description',
    addLine: 'Add line',
    removeLine: 'Remove line',
    preview: 'Entry preview',
    previewLead: 'The same code the application posts with — on the accounts of the chosen template and sample amounts.',
    onTemplate: 'On chart template',
    sampleNet: 'Net',
    sampleVat: 'VAT',
    sampleDeductible: 'Deductible VAT',
    sampleCounterparty: 'Document has a counterparty',
    account: 'Account',
    amount: 'Amount',
    debit: 'Dr',
    credit: 'Cr',
    total: 'Total',
    postable: 'The entry balances and will pass through the journal.',
    notPostable: 'The entry will not pass:',
    previewEmpty: 'Fill in the lines to see the entry.',
    save: 'Save scheme',
    saveVersion: 'Save new version',
    newVersion: 'New version from a date',
    versionLead: 'The previous version ends the day before. Documents dated earlier keep posting the old way.',
    rename: 'Name and visibility',
    activeLabel: 'Active — the application proposes this scheme',
    inactive: 'disabled',
    closed: 'ended',
    future: 'from',
    current: 'in force',
    saved: 'Saved.',
    back: 'Back to the list',
    cancel: 'Cancel',
    settlementHint: 'A settlement account always carries the counterparty — without it the entry will not pass through the journal.',
  },
};

const dictionaries: Record<Locale, Dictionary> = { pl, en };

export function dictionary(locale: Locale): Dictionary {
  return dictionaries[locale];
}

const LOCALE_KEY = 'firmeto-panel-jezyk';

export function detectLocale(): Locale {
  try {
    const saved = window.localStorage.getItem(LOCALE_KEY);
    if (saved && (locales as readonly string[]).includes(saved)) return saved as Locale;
  } catch {
    // brak pamięci lokalnej — zostajemy przy domyślnym
  }
  return defaultLocale;
}

export function rememberLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    // tryb prywatny
  }
}

/** Kwota w złotych w zapisie danego języka. */
export function money(value: string | number, locale: Locale): string {
  return new Intl.NumberFormat(locale === 'pl' ? 'pl-PL' : 'en-GB', { style: 'currency', currency: 'PLN' }).format(Number(value));
}

/** Data dzienna; puste, gdy brak. */
export function date(value: string | null | undefined, locale: Locale): string {
  if (!value) return '';
  return new Intl.DateTimeFormat(locale === 'pl' ? 'pl-PL' : 'en-GB', { dateStyle: 'medium' }).format(new Date(value));
}

/** NIP w zapisie z myślnikami: 123-456-78-90. */
export function nip(value: string): string {
  return value.replace(/^(\d{3})(\d{3})(\d{2})(\d{2})$/, '$1-$2-$3-$4');
}
